import React, { useState, useEffect, useCallback, useContext } from "react";
import { format, formatDistanceToNow, parseISO, differenceInMinutes } from "date-fns";
import { ptBR } from "date-fns/locale";

import { makeStyles } from "@material-ui/core/styles";
import Paper from "@material-ui/core/Paper";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import TableSortLabel from "@material-ui/core/TableSortLabel";
import Chip from "@material-ui/core/Chip";
import TextField from "@material-ui/core/TextField";
import MenuItem from "@material-ui/core/MenuItem";
import Select from "@material-ui/core/Select";
import FormControl from "@material-ui/core/FormControl";
import InputLabel from "@material-ui/core/InputLabel";
import Drawer from "@material-ui/core/Drawer";
import Typography from "@material-ui/core/Typography";
import Divider from "@material-ui/core/Divider";
import IconButton from "@material-ui/core/IconButton";
import CloseIcon from "@material-ui/icons/Close";
import Button from "@material-ui/core/Button";
import Timeline from "@material-ui/lab/Timeline";
import TimelineItem from "@material-ui/lab/TimelineItem";
import TimelineSeparator from "@material-ui/lab/TimelineSeparator";
import TimelineConnector from "@material-ui/lab/TimelineConnector";
import TimelineContent from "@material-ui/lab/TimelineContent";
import TimelineDot from "@material-ui/lab/TimelineDot";
import SwapHorizIcon from "@material-ui/icons/SwapHoriz";
import BuildIcon from "@material-ui/icons/Build";
import HourglassEmptyIcon from "@material-ui/icons/HourglassEmpty";
import PauseCircleOutlineIcon from "@material-ui/icons/PauseCircleOutline";
import AssignmentTurnedInIcon from "@material-ui/icons/AssignmentTurnedIn";
import FiberManualRecordIcon from "@material-ui/icons/FiberManualRecord";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import SlaIndicator from "../../components/SlaIndicator";
import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";
import toastError from "../../errors/toastError";
import { toast } from "react-toastify";

const PRIORITY_COLORS = {
  low: "#4caf50",
  medium: "#2196f3",
  high: "#ff9800",
  critical: "#f44336",
};

const STATUS_COLORS = {
  pending: "#9e9e9e",
  open: "#2196f3",
  waiting: "#ff9800",
  resolved: "#8bc34a",
  closed: "#607d8b",
  "in-progress": "#1565c0",
  "waiting-vendor": "#7b1fa2",
};

const STATUS_ICONS = {
  open: <FiberManualRecordIcon style={{ fontSize: 14 }} />,
  pending: <FiberManualRecordIcon style={{ fontSize: 14 }} />,
  waiting: <PauseCircleOutlineIcon style={{ fontSize: 14 }} />,
  "waiting-vendor": <HourglassEmptyIcon style={{ fontSize: 14 }} />,
  "in-progress": <BuildIcon style={{ fontSize: 14 }} />,
  resolved: <AssignmentTurnedInIcon style={{ fontSize: 14 }} />,
  closed: <AssignmentTurnedInIcon style={{ fontSize: 14 }} />,
};

const ALL_STATUSES = ["pending", "open", "in-progress", "waiting", "waiting-vendor", "resolved", "closed"];

const statusLabel = (s) => i18n.t(`chamados.status.${s}`, { defaultValue: s });
const typeLabel = (t) => i18n.t(`chamados.type.${t}`, { defaultValue: t });

const formatMinutes = (mins) => {
  if (!mins && mins !== 0) return "—";
  if (mins < 60) return `${mins}min`;
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  return m > 0 ? `${h}h ${m}min` : `${h}h`;
};

const useStyles = makeStyles((theme) => ({
  root: { padding: theme.spacing(2) },
  paper: { overflowX: "auto" },
  table: { minWidth: 1000 },
  filterBar: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(2),
    flexWrap: "wrap",
    alignItems: "flex-end",
  },
  formControl: { minWidth: 160 },
  drawerPaper: { width: 520, padding: theme.spacing(2) },
  drawerHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(2),
  },
  sectionTitle: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(1),
    fontWeight: "bold",
  },
  messageBox: {
    background: "#f5f5f5",
    borderRadius: 4,
    padding: theme.spacing(1),
    marginBottom: theme.spacing(1),
    fontSize: "0.85rem",
  },
  actionButtons: {
    display: "flex",
    gap: theme.spacing(1),
    marginTop: theme.spacing(2),
    flexWrap: "wrap",
  },
  historyItem: {
    padding: theme.spacing(0.25, 0),
    fontSize: "0.78rem",
  },
  historyDuration: {
    color: theme.palette.text.hint,
    fontSize: "0.72rem",
  },
}));

const Chamados = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);
  const [equipments, setEquipments] = useState([]);

  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [filterType, setFilterType] = useState("");
  const [filterEquipment, setFilterEquipment] = useState("");
  const [searchParam, setSearchParam] = useState("");

  const [orderBy, setOrderBy] = useState("createdAt");
  const [order, setOrder] = useState("desc");

  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [statusHistory, setStatusHistory] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);

  useEffect(() => {
    api.get("/equipment").then(({ data }) => setEquipments(data)).catch(() => {});
  }, []);

  const fetchTickets = useCallback(async () => {
    setLoading(true);
    try {
      const userQueueIds = (user?.queues || []).map(q => q.id);
      const params = {
        showAll: "true",
        pageNumber: 1,
        queueIds: JSON.stringify(userQueueIds),
        ...(filterStatus && { status: filterStatus }),
        ...(filterPriority && { priorities: JSON.stringify([filterPriority]) }),
        ...(filterType && { types: JSON.stringify([filterType]) }),
        ...(filterEquipment && { equipmentIds: JSON.stringify([Number(filterEquipment)]) }),
        ...(searchParam && { searchParam }),
      };
      const { data } = await api.get("/tickets", { params });
      setTickets(data.tickets);
    } catch (err) {
      toastError(err);
    } finally {
      setLoading(false);
    }
  }, [filterStatus, filterPriority, filterType, filterEquipment, searchParam, user]);

  useEffect(() => {
    const timer = setTimeout(fetchTickets, 400);
    return () => clearTimeout(timer);
  }, [fetchTickets]);

  const handleSort = (column) => {
    if (orderBy === column) {
      setOrder(order === "asc" ? "desc" : "asc");
    } else {
      setOrderBy(column);
      setOrder("asc");
    }
  };

  const sortedTickets = [...tickets].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (typeof aVal === "string") aVal = aVal.toLowerCase();
    if (typeof bVal === "string") bVal = bVal.toLowerCase();
    if (aVal < bVal) return order === "asc" ? -1 : 1;
    if (aVal > bVal) return order === "asc" ? 1 : -1;
    return 0;
  });

  const openDetail = async (ticket) => {
    setSelectedTicket(ticket);
    setDrawerOpen(true);
    setMessages([]);
    setStatusHistory([]);
    try {
      const [msgRes, histRes] = await Promise.all([
        api.get(`/messages/${ticket.id}`).catch(() => ({ data: { messages: [] } })),
        api.get(`/tickets/${ticket.id}/status-history`).catch(() => ({ data: [] })),
      ]);
      setMessages(msgRes.data.messages || []);
      setStatusHistory(histRes.data || []);
    } catch (_) {}
  };

  const handleStatusChange = async (ticketId, status) => {
    try {
      await api.put(`/tickets/${ticketId}`, { status });
      toast.success("Status atualizado");
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket((t) => ({ ...t, status }));
        const histRes = await api.get(`/tickets/${ticketId}/status-history`).catch(() => ({ data: [] }));
        setStatusHistory(histRes.data || []);
      }
    } catch (err) {
      toastError(err);
    }
  };

  const handlePriorityChange = async (ticketId, priority) => {
    try {
      await api.put(`/tickets/${ticketId}`, { priority });
      toast.success("Prioridade atualizada");
      fetchTickets();
      if (selectedTicket?.id === ticketId) {
        setSelectedTicket((t) => ({ ...t, priority }));
      }
    } catch (err) {
      toastError(err);
    }
  };

  const sortLabel = (col, label) => (
    <TableSortLabel
      active={orderBy === col}
      direction={orderBy === col ? order : "asc"}
      onClick={() => handleSort(col)}
    >
      {label}
    </TableSortLabel>
  );

  const renderStatusHistory = () => {
    if (statusHistory.length === 0) {
      return (
        <Typography variant="body2" color="textSecondary">
          Sem histórico registrado.
        </Typography>
      );
    }

    return (
      <Timeline style={{ padding: 0, margin: 0 }}>
        {statusHistory.map((h, idx) => {
          const next = statusHistory[idx + 1];
          const durationMins = next
            ? differenceInMinutes(parseISO(next.changedAt), parseISO(h.changedAt))
            : null;

          const isQueueChange = h.fromQueueId !== h.toQueueId && h.fromStatus === h.toStatus;
          const isStatusChange = h.fromStatus !== h.toStatus;

          return (
            <TimelineItem key={h.id} style={{ minHeight: 48 }}>
              <TimelineSeparator>
                <TimelineDot
                  style={{
                    backgroundColor: STATUS_COLORS[h.toStatus] || "#9e9e9e",
                    width: 10,
                    height: 10,
                    margin: "4px 0",
                  }}
                >
                  {isQueueChange ? <SwapHorizIcon style={{ fontSize: 8 }} /> : null}
                </TimelineDot>
                {idx < statusHistory.length - 1 && <TimelineConnector style={{ minHeight: 20 }} />}
              </TimelineSeparator>
              <TimelineContent style={{ padding: "0 8px", paddingTop: 2 }}>
                <div className={classes.historyItem}>
                  {isQueueChange && !isStatusChange ? (
                    <span>
                      Transferido:{" "}
                      <strong>{h.fromQueueName || `#${h.fromQueueId}`}</strong>
                      {" → "}
                      <strong>{h.toQueueName || `#${h.toQueueId}`}</strong>
                    </span>
                  ) : (
                    <span>
                      <Chip
                        label={statusLabel(h.fromStatus)}
                        size="small"
                        style={{
                          backgroundColor: STATUS_COLORS[h.fromStatus] || "#9e9e9e",
                          color: "#fff",
                          fontSize: "0.65rem",
                          height: 18,
                          marginRight: 4,
                        }}
                      />
                      {"→ "}
                      <Chip
                        label={statusLabel(h.toStatus)}
                        size="small"
                        style={{
                          backgroundColor: STATUS_COLORS[h.toStatus] || "#9e9e9e",
                          color: "#fff",
                          fontSize: "0.65rem",
                          height: 18,
                        }}
                      />
                    </span>
                  )}
                  {h.user && (
                    <span style={{ marginLeft: 4, color: "#888" }}>
                      {" "}por {h.user.name}
                    </span>
                  )}
                </div>
                <div className={classes.historyDuration}>
                  {format(parseISO(h.changedAt), "dd/MM HH:mm")}
                  {durationMins !== null && (
                    <span style={{ marginLeft: 6 }}>
                      • ficou {formatMinutes(durationMins)}
                    </span>
                  )}
                </div>
              </TimelineContent>
            </TimelineItem>
          );
        })}
      </Timeline>
    );
  };

  return (
    <MainContainer>
      <MainHeader>
        <Title>{i18n.t("chamados.title")}</Title>
      </MainHeader>

      <div className={classes.root}>
        <div className={classes.filterBar}>
          <TextField
            label="Buscar"
            variant="outlined"
            size="small"
            value={searchParam}
            onChange={(e) => setSearchParam(e.target.value)}
          />

          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="">Todos</MenuItem>
              {ALL_STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  <Chip
                    label={statusLabel(s)}
                    size="small"
                    style={{ backgroundColor: STATUS_COLORS[s] || "#9e9e9e", color: "#fff", fontSize: "0.7rem" }}
                  />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Prioridade</InputLabel>
            <Select
              value={filterPriority}
              onChange={(e) => setFilterPriority(e.target.value)}
              label="Prioridade"
            >
              <MenuItem value="">Todas</MenuItem>
              {["low", "medium", "high", "critical"].map((p) => (
                <MenuItem key={p} value={p}>
                  {i18n.t(`priority.${p}`)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl variant="outlined" size="small" className={classes.formControl}>
            <InputLabel>Tipo</InputLabel>
            <Select
              value={filterType}
              onChange={(e) => setFilterType(e.target.value)}
              label="Tipo"
            >
              <MenuItem value="">Todos</MenuItem>
              <MenuItem value="incident">{typeLabel("incident")}</MenuItem>
              <MenuItem value="service_request">{typeLabel("service_request")}</MenuItem>
            </Select>
          </FormControl>

          {equipments.length > 0 && (
            <FormControl variant="outlined" size="small" className={classes.formControl}>
              <InputLabel>Equipamento</InputLabel>
              <Select
                value={filterEquipment}
                onChange={(e) => setFilterEquipment(e.target.value)}
                label="Equipamento"
              >
                <MenuItem value="">Todos</MenuItem>
                {equipments.map((eq) => (
                  <MenuItem key={eq.id} value={eq.id}>
                    {eq.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </div>

        <Paper className={classes.paper}>
          <Table className={classes.table} size="small">
            <TableHead>
              <TableRow>
                <TableCell>{sortLabel("ticketNumber", i18n.t("chamados.columns.number"))}</TableCell>
                <TableCell>{sortLabel("contact.name", i18n.t("chamados.columns.contact"))}</TableCell>
                <TableCell>Tipo</TableCell>
                <TableCell>Equipamento</TableCell>
                <TableCell>Categoria (Tags)</TableCell>
                <TableCell>{sortLabel("priority", i18n.t("chamados.columns.priority"))}</TableCell>
                <TableCell>{sortLabel("status", i18n.t("chamados.columns.status"))}</TableCell>
                <TableCell>Técnico</TableCell>
                <TableCell>{sortLabel("createdAt", i18n.t("chamados.columns.openedAt"))}</TableCell>
                <TableCell>Tempo em aberto</TableCell>
                <TableCell>SLA</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={11} align="center">Carregando...</TableCell>
                </TableRow>
              ) : sortedTickets.map((ticket) => (
                <TableRow
                  key={ticket.id}
                  hover
                  style={{ cursor: "pointer" }}
                  onClick={() => openDetail(ticket)}
                >
                  <TableCell>
                    <Typography variant="caption" color="textSecondary">
                      {ticket.ticketNumber || `#${ticket.id}`}
                    </Typography>
                  </TableCell>
                  <TableCell>{ticket.contact?.name}</TableCell>
                  <TableCell>
                    <Typography variant="caption">
                      {typeLabel(ticket.type || "incident")}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Typography variant="caption" color="textSecondary">
                      {ticket.equipment?.name || "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    {ticket.tags?.map((tag) => (
                      <Chip
                        key={tag.id}
                        label={tag.name}
                        size="small"
                        style={{ backgroundColor: tag.color, color: "#fff", marginRight: 2 }}
                      />
                    ))}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={i18n.t(`priority.${ticket.priority || "medium"}`)}
                      size="small"
                      style={{
                        backgroundColor: PRIORITY_COLORS[ticket.priority || "medium"],
                        color: "#fff",
                        fontWeight: "bold",
                      }}
                    />
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={statusLabel(ticket.status)}
                      size="small"
                      style={{
                        backgroundColor: STATUS_COLORS[ticket.status] || "#9e9e9e",
                        color: "#fff",
                      }}
                    />
                  </TableCell>
                  <TableCell>{ticket.user?.name || "—"}</TableCell>
                  <TableCell>
                    {ticket.createdAt
                      ? format(parseISO(ticket.createdAt), "dd/MM/yyyy HH:mm")
                      : "—"}
                  </TableCell>
                  <TableCell>
                    {ticket.createdAt && ticket.status !== "closed"
                      ? formatDistanceToNow(parseISO(ticket.createdAt), { locale: ptBR, addSuffix: false })
                      : "—"}
                  </TableCell>
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <SlaIndicator ticketId={ticket.id} priority={ticket.priority} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      </div>

      <Drawer
        anchor="right"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        classes={{ paper: classes.drawerPaper }}
      >
        {selectedTicket && (
          <>
            <div className={classes.drawerHeader}>
              <Typography variant="h6">
                {selectedTicket.ticketNumber || `#${selectedTicket.id}`} — {selectedTicket.contact?.name}
              </Typography>
              <IconButton onClick={() => setDrawerOpen(false)}>
                <CloseIcon />
              </IconButton>
            </div>

            <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
              <Chip
                label={i18n.t(`priority.${selectedTicket.priority || "medium"}`)}
                size="small"
                style={{
                  backgroundColor: PRIORITY_COLORS[selectedTicket.priority || "medium"],
                  color: "#fff",
                  fontWeight: "bold",
                }}
              />
              <Chip
                label={statusLabel(selectedTicket.status)}
                size="small"
                style={{
                  backgroundColor: STATUS_COLORS[selectedTicket.status] || "#9e9e9e",
                  color: "#fff",
                }}
              />
              <Chip
                label={typeLabel(selectedTicket.type || "incident")}
                size="small"
                variant="outlined"
              />
              {selectedTicket.equipment && (
                <Chip
                  label={selectedTicket.equipment.name}
                  size="small"
                  variant="outlined"
                />
              )}
            </div>

            {selectedTicket.queue && (
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 6 }}>
                Fila: <strong>{selectedTicket.queue.name}</strong>
                {selectedTicket.user && (
                  <span> • Técnico: <strong>{selectedTicket.user.name}</strong></span>
                )}
              </Typography>
            )}

            <Typography className={classes.sectionTitle}>Alterar prioridade</Typography>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {["low", "medium", "high", "critical"].map((p) => (
                <Button
                  key={p}
                  size="small"
                  variant={selectedTicket.priority === p ? "contained" : "outlined"}
                  style={{
                    borderColor: PRIORITY_COLORS[p],
                    color: selectedTicket.priority === p ? "#fff" : PRIORITY_COLORS[p],
                    backgroundColor: selectedTicket.priority === p ? PRIORITY_COLORS[p] : "transparent",
                  }}
                  onClick={() => handlePriorityChange(selectedTicket.id, p)}
                >
                  {i18n.t(`priority.${p}`)}
                </Button>
              ))}
            </div>

            <Divider style={{ margin: "16px 0" }} />

            <Typography className={classes.sectionTitle}>
              SLA
            </Typography>
            <SlaIndicator ticketId={selectedTicket.id} priority={selectedTicket.priority} />

            <Divider style={{ margin: "16px 0" }} />

            <Typography className={classes.sectionTitle}>
              {i18n.t("chamados.history.title")}
            </Typography>
            {renderStatusHistory()}

            <Divider style={{ margin: "16px 0" }} />

            <Typography className={classes.sectionTitle}>Histórico de mensagens</Typography>
            <div style={{ maxHeight: 250, overflowY: "auto" }}>
              {messages.length === 0 && (
                <Typography variant="body2" color="textSecondary">
                  Sem mensagens registradas.
                </Typography>
              )}
              {messages.map((msg) => (
                <div
                  key={msg.id}
                  className={classes.messageBox}
                  style={{ textAlign: msg.fromMe ? "right" : "left" }}
                >
                  <Typography variant="caption" color="textSecondary" display="block">
                    {msg.fromMe ? "Atendente" : selectedTicket.contact?.name} —{" "}
                    {msg.createdAt
                      ? format(parseISO(msg.createdAt), "dd/MM HH:mm")
                      : ""}
                  </Typography>
                  {msg.body}
                </div>
              ))}
            </div>

            <Divider style={{ margin: "16px 0" }} />

            <div className={classes.actionButtons}>
              {selectedTicket.status === "open" && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ borderColor: "#1565c0", color: "#1565c0" }}
                    onClick={() => handleStatusChange(selectedTicket.id, "in-progress")}
                  >
                    {i18n.t("chamados.buttons.internalWork")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ borderColor: "#7b1fa2", color: "#7b1fa2" }}
                    onClick={() => handleStatusChange(selectedTicket.id, "waiting-vendor")}
                  >
                    {i18n.t("chamados.buttons.waitVendor")}
                  </Button>
                  <Button
                    size="small"
                    variant="outlined"
                    color="default"
                    onClick={() => handleStatusChange(selectedTicket.id, "waiting")}
                  >
                    {i18n.t("chamados.buttons.waitUser")}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    style={{ backgroundColor: "#8bc34a", color: "#fff" }}
                    onClick={() => handleStatusChange(selectedTicket.id, "resolved")}
                  >
                    {i18n.t("chamados.buttons.markResolved")}
                  </Button>
                </>
              )}
              {["in-progress", "waiting-vendor", "waiting"].includes(selectedTicket.status) && (
                <>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => handleStatusChange(selectedTicket.id, "open")}
                  >
                    {i18n.t("chamados.buttons.resume")}
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    style={{ backgroundColor: "#8bc34a", color: "#fff" }}
                    onClick={() => handleStatusChange(selectedTicket.id, "resolved")}
                  >
                    {i18n.t("chamados.buttons.markResolved")}
                  </Button>
                </>
              )}
              {["open", "in-progress", "waiting-vendor", "waiting", "resolved"].includes(selectedTicket.status) && (
                <Button
                  size="small"
                  variant="contained"
                  color="secondary"
                  onClick={() => handleStatusChange(selectedTicket.id, "closed")}
                >
                  {i18n.t("chamados.buttons.close")}
                </Button>
              )}
            </div>
          </>
        )}
      </Drawer>
    </MainContainer>
  );
};

export default Chamados;
